import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { HolidayRules, HolidaySource, holidayYearsOf } from './hr-holidays';
import { frenchHolidaysOf } from './hr-schedule-engine';

// Le calendrier OFFICIEL des jours fériés — API Etalab `calendrier.api.gouv.fr`.
//
// Pourquoi un appel direct depuis le navigateur : l'API est publique, sans clé, et renvoie
// `access-control-allow-origin: *`. Aucun proxy serveur à écrire, donc aucune dépendance de
// déploiement pour une donnée… de calendrier.
//
// Pourquoi un REPLI local : cette API est un service tiers. Si elle est indisponible (ou si le
// poste n'a pas Internet), `frenchHolidaysOf` calcule les 11 fériés de métropole hors ligne —
// Pâques comprise. On ne bloque jamais un écran de planning pour ça ; on note seulement que la
// source est locale, ce que l'écran Organisation affiche.
//
// Ce que l'API apporte que le calcul local ne sait pas faire : les ZONES (l'Alsace-Moselle a le
// Vendredi saint et le 26 décembre en plus) et les libellés officiels.
//
// POURQUOI PAS `HttpClient` TEL QUEL — l'`AuthInterceptor` de l'application colle le JWT
// Mercasell sur TOUTE requête sortante. Ici, cela ferait deux dégâts : envoyer le jeton de
// session à un tiers, et déclencher un pré-vol CORS que l'API refuse (elle répond 405 sans
// en-têtes CORS à OPTIONS — l'appel échouerait donc SYSTÉMATIQUEMENT une fois connecté, et le
// service retomberait en silence sur le calcul local). On passe donc par `HttpBackend`, qui
// court-circuite les intercepteurs : cet appel part sans en-tête d'authentification, comme il
// se doit — même motif que hr-idcc.service.ts.
@Injectable({ providedIn: 'root' })
export class HrHolidaysService {

	// Client SANS intercepteurs — cf. l'en-tête du fichier.
	private http = new HttpClient(inject(HttpBackend));

	private static readonly BASE = 'https://calendrier.api.gouv.fr/jours-feries/';
	private static readonly TIMEOUT_MS = 6000;

	// Fériés des années couvrant l'horizon des règles. Rend TOUJOURS un calendrier : celui de
	// l'API si elle répond, le calcul local sinon (`source` dit lequel).
	fetch$(rules: HolidayRules, today: string): Observable<{ days: Map<string, string>; source: HolidaySource }> {
		const years = holidayYearsOf(today, rules);
		const calls = years.map(year => this.http
			.get<{ [dat: string]: string }>(HrHolidaysService.BASE + rules.zone + '/' + year + '.json')
			.pipe(
				timeout(HrHolidaysService.TIMEOUT_MS),
				map(res => ({ year, entries: Object.entries(res ?? {}), ok: true })),
				catchError(err => {
					console.warn('[HrHolidaysService] ' + rules.zone + '/' + year + ' indisponible — repli sur le calcul local', err?.status ?? err?.name);
					return of({ year, entries: [] as [string, string][], ok: false });
				}),
			));

		return forkJoin(calls).pipe(map(results => {
			const days = new Map<string, string>();
			let allOk = true;
			for (const r of results) {
				if (r.ok && r.entries.length > 0) {
					for (const [dat, label] of r.entries) days.set(dat, label);
				} else {
					allOk = false;
					// Repli année par année : une seule année manquante ne fait pas tomber les autres.
					for (const [dat, label] of frenchHolidaysOf(r.year)) days.set(dat, label);
				}
			}
			return { days, source: (allOk ? 'api' : 'local') as HolidaySource };
		}));
	}
}
